import { formatUtcOffset } from '@felix/domain';
import { useTranslation } from 'react-i18next';
import classes from './RestHoursScreen.module.css';
import type { DaySegment } from './rest-hours.js';
import { colorOf } from './rest-ui.js';
import { clockText, type DayAxis, type DaySpan } from './ship-clock.js';

/**
 * Saat değişen günün bandı (ADR 0065 §7; ortağın "Crew Work & Rest Hours" önerisi): eksen gerçek
 * süredir. Değişiklik anında dikey "Clock adjustment" işareti; geri almada tekrar eden dilim
 * "23:00 (2nd)" etiketli ve çerçeveli; ileri almada yaşanmayan duvar saati kendi genişliğinde
 * taralı blok. Renk tek başına anlam taşımaz: desen, etiket ve bandın altındaki metin aynı bilgiyi
 * verir; ekran okuyucu için her parça adlandırılır.
 */
export function ClockDayBand({
  span,
  axis,
  segments,
  nowMinute,
  label,
  activityLabel,
  formatDuration,
}: {
  span: DaySpan;
  axis: DayAxis;
  segments: readonly DaySegment[];
  /** Gemi saatiyle şimdi, gerçek dakika (gün bugünse). */
  nowMinute: number | null;
  /** Gerçek dakikanın duvar saati etiketi ("23:00 (2nd)"). */
  label: (real: number, side?: 'start' | 'end') => string;
  activityLabel: (activity: string | null) => string;
  formatDuration: (minutes: number) => string;
}) {
  const { t } = useTranslation();
  const occurrence = (value: 1 | 2 | null) =>
    value ? ` ${t(`restHours.clock.occurrence${value}`)}` : '';
  const percent = (display: number) => `${(display / axis.length) * 100}%`;
  const position = (real: number) => {
    let display = real;
    for (const block of axis.blocks) {
      if (block.kind === 'skipped' && block.from <= real) display += block.width;
    }
    return display;
  };

  // Bant öğeleri: blok sırasıyla; zaman bloklarında o bloğa düşen parçalar
  const items: (
    | { kind: 'skipped'; key: string; width: number; from: number; to: number }
    | { kind: 'segment'; key: string; segment: DaySegment; from: number; to: number }
  )[] = [];
  for (const block of axis.blocks) {
    if (block.kind === 'skipped') {
      items.push({
        kind: 'skipped',
        key: `skip-${block.at}`,
        width: block.width,
        from: block.wallFrom,
        to: block.wallTo,
      });
      continue;
    }
    for (const segment of segments) {
      const from = Math.max(segment.from, block.from);
      const to = Math.min(segment.to, block.to);
      if (to > from) items.push({ kind: 'segment', key: `${from}-${to}`, segment, from, to });
    }
  }

  const sentence = (a: DayAxis['markers'][number]) =>
    t(`restHours.clock.${a.changeMin < 0 ? 'retarded' : 'advanced'}`, {
      minutes: Math.abs(a.changeMin),
      // Gece yarısındaki değişiklik "00:00" (günün 24:00'ı)
      time: clockText(a.wallBefore >= 1440 ? a.wallBefore - 1440 : a.wallBefore),
      from: formatUtcOffset(a.oldOffsetMin),
      to: formatUtcOffset(a.newOffsetMin),
    });
  const notes = [
    ...axis.markers.map(sentence),
    ...span.timeline.repeated.map((r) =>
      t('restHours.clock.repeated', { from: clockText(r.wallFrom), to: clockText(r.wallTo) }),
    ),
    ...span.timeline.skipped.map((g) =>
      t('restHours.clock.notLived', { from: clockText(g.wallFrom), to: clockText(g.wallTo) }),
    ),
    ...(span.repeatedDate ? [t('restHours.clock.dateRepeated')] : []),
  ];

  return (
    <>
      <div className={`${classes.hours} ${classes.clockHours}`} aria-hidden>
        {axis.ticks.map((tick) => (
          <span
            key={tick.at}
            // Yalnız eksenin uçları hizalanır; saat ayarı etiketi işaretin üstünde ortalanır
            data-edge={tick.at === 0 ? 'start' : tick.at === axis.length ? 'end' : undefined}
            // Dar ekranda yalnız dört saatte bir ve saat ayarı etiketleri (blok başı, gün sonu)
            data-minor={
              (tick.wall % 240 !== 0 &&
                !axis.blocks.some((b) => b.at === tick.at) &&
                tick.at !== axis.length) ||
              undefined
            }
            data-repeat={tick.occurrence === 2 || undefined}
            style={{ left: percent(tick.at) }}
          >
            {clockText(tick.wall)}
            {tick.occurrence ? (
              <small className={classes.tickOccurrence}>{occurrence(tick.occurrence)}</small>
            ) : null}
          </span>
        ))}
      </div>
      <div className={classes.bandWrap} data-clock>
        <ol className={classes.band}>
          {items.map((item) =>
            item.kind === 'skipped' ? (
              <li
                key={item.key}
                className={classes.segment}
                data-kind="skipped"
                style={{ flexGrow: item.width }}
              >
                <span className={classes.visuallyHidden}>
                  {t('restHours.clock.notLived', {
                    from: clockText(item.from),
                    to: clockText(item.to),
                  })}
                </span>
              </li>
            ) : (
              <li
                key={item.key}
                className={classes.segment}
                data-kind={item.segment.kind}
                data-short={item.segment.short || undefined}
                style={{
                  flexGrow: item.to - item.from,
                  background: colorOf(item.segment.kind === 'work' ? item.segment.activity : null),
                }}
              >
                <span className={classes.visuallyHidden}>
                  {t('restHours.segment', {
                    kind: activityLabel(
                      item.segment.kind === 'work' ? item.segment.activity : null,
                    ),
                    from: label(item.from),
                    to: label(item.to, 'end'),
                    duration: formatDuration(item.to - item.from),
                  })}
                </span>
              </li>
            ),
          )}
        </ol>
        {axis.repeated.map((r) => (
          <span
            key={`repeat-${r.at}`}
            className={classes.repeat}
            style={{ left: percent(r.at), width: percent(r.width) }}
            title={`${clockText(r.wallFrom)}–${clockText(r.wallTo)}${occurrence(2)}`}
            aria-hidden
          />
        ))}
        {axis.markers.map((m) => (
          <span
            key={`marker-${m.position}`}
            className={classes.marker}
            style={{ left: percent(m.position) }}
            title={sentence(m)}
            aria-hidden
          >
            <span className={classes.markerLabel}>{t('restHours.clock.adjustment')}</span>
          </span>
        ))}
        {nowMinute !== null && nowMinute >= 0 && nowMinute <= span.minutes ? (
          <span
            className={classes.now}
            style={{ left: percent(position(nowMinute)) }}
            title={t('restHours.now')}
            aria-hidden
          />
        ) : null}
      </div>
      {notes.length > 0 ? (
        <ul className={classes.clockNotes} aria-label={t('restHours.clock.adjustment')}>
          {notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      ) : null}
    </>
  );
}
